import { Hono } from 'hono';

import { cors } from 'hono/cors';
import { secureHeaders } from 'hono/secure-headers';
import { logger as honoLogger } from 'hono/logger';
import { timing } from 'hono/timing';
import { bodyLimit } from 'hono/body-limit';
import { HTTPException } from 'hono/http-exception';
import { config } from './config/index.js';
import { getApiInfo } from './config/api-info.js';
import type { FormatAdapter } from '@json-to-office/jto-cli';

import { healthRouter } from './routes/health.js';
import { createFormatRouter } from './routes/format.js';
import { discoveryRouter } from './routes/discovery.js';
import { createAiRouter } from './routes/ai.js';
import { fontsRouter } from './routes/fonts.js';

import { requestIdMiddleware } from './middleware/hono/request-id.js';
import { apiKeyAuthMiddleware } from './middleware/hono/auth.js';
import { errorHandler } from './middleware/hono/error-handler.js';
import { securityMiddleware } from './middleware/hono/security.js';
import { errorRecoveryMiddleware } from './middleware/hono/error-recovery.js';
import { requestLoggerMiddleware } from './middleware/hono/request-logger.js';
import { rateLimiter } from './middleware/hono/rate-limit.js';
import { concurrencyLimiter } from './middleware/hono/concurrency-limit.js';

import { AppEnv } from './types/hono.js';
import { Container } from './container/index.js';

export function createAPIApp(adapter: FormatAdapter) {
  // Initialize DI container with the adapter
  Container.initialize(adapter);

  const honoApp = new Hono<AppEnv>();

  // Built-in middleware
  honoApp.use('*', timing());
  honoApp.use('*', honoLogger());

  // CORS
  honoApp.use(
    '*',
    cors({
      origin: config.cors.origin,
      credentials: config.cors.origin === '*' ? false : config.cors.credentials,
      allowMethods: ['GET', 'POST', 'PUT', 'DELETE', 'OPTIONS'],
      allowHeaders: ['Content-Type', 'Authorization', config.API_KEY_HEADER],
      exposeHeaders: ['X-Request-Id', 'X-File-Id'],
      maxAge: 86400,
    })
  );

  honoApp.use('*', secureHeaders());

  // Custom middleware
  honoApp.use('*', requestIdMiddleware);
  honoApp.use('*', errorRecoveryMiddleware);
  honoApp.use('*', requestLoggerMiddleware);
  honoApp.use('*', securityMiddleware);

  // Uniform admission controls cover every API surface. Individual expensive
  // routes retain tighter limits below this process-wide ceiling.
  //
  // These run BEFORE authentication on purpose: Hono executes middleware in
  // registration order, so an auth check registered first would return 401
  // without ever reaching the limiter, leaving API-key guessing unbounded.
  honoApp.use(
    '/api/*',
    bodyLimit({
      maxSize: config.requestLimits.maxBodySize,
      onError: () => {
        throw new HTTPException(413, { message: 'Request body too large' });
      },
    })
  );
  honoApp.use(
    '/api/*',
    rateLimiter({
      limit: config.rateLimit.max,
      window: config.rateLimit.windowMs,
      namespace: (c) => `${c.req.method}:${c.req.path}`,
      trustProxy: config.rateLimit.trustProxy,
    })
  );
  honoApp.use(
    '/api/*',
    concurrencyLimiter({ limit: config.requestLimits.maxConcurrent })
  );

  // API key auth (if enabled)
  if (config.features.apiKey) {
    honoApp.use('/api/*', apiKeyAuthMiddleware);
  }

  // Mount routes
  honoApp.route('/health', healthRouter);

  // Format-specific routes at /api/{format}/*
  const formatRouter = createFormatRouter(adapter);
  honoApp.route(`/api/${adapter.name}`, formatRouter);

  // Also mount at /api/documents or /api/presentations for backward compat
  const legacyPath =
    adapter.name === 'docx' ? '/api/documents' : '/api/presentations';
  honoApp.route(legacyPath, formatRouter);

  // Discovery routes
  honoApp.route('/api/discovery', discoveryRouter);

  // Font catalog + Google Fonts materialization
  honoApp.route('/api/fonts', fontsRouter);

  // AI chat routes (disabled via AI_ENABLED=false)
  if (process.env.AI_ENABLED !== 'false') {
    honoApp.route('/api/ai', createAiRouter());
  }

  // Root endpoint
  honoApp.get('/', async (c) => {
    const apiInfo = await getApiInfo();
    return c.json(apiInfo);
  });

  honoApp.get('/api', async (c) => {
    const apiInfo = await getApiInfo();
    return c.json(apiInfo);
  });

  // 404 handler
  honoApp.notFound((c) => {
    return c.json(
      {
        success: false,
        error: 'Not found',
        path: c.req.path,
        method: c.req.method,
        requestId: c.get('requestId'),
      },
      404
    );
  });

  honoApp.onError(errorHandler);

  return honoApp;
}
